import React, { memo } from 'react'
import { Box, Card, Divider, Skeleton, Stack } from '@mui/material'
import InfiniteScroll from 'react-infinite-scroll-component'

import customConfig from 'customConfig'
import SearchBar from '../shared/searchBar/SearchBar'
import CashbackBrandCard from './CashbackBrandCard'
import RecordsNotFound from '../shared/recordsNotFound/RecordsNotFound'
import { LoadingScreen } from '../shared/loading-screen'
import CashbackFilters from './CashbackFilters'
import CashbackFiltersResult from './CashbackFiltersResult'
import PageTitle from '../shared/pageTitle/PageTitle'

const Cashback = ({
  t,
  cashbackBrands,
  onCashbackShopNowClick,
  loading,
  checkedCategoriesForFilter,
  filterCategories,
  query,
  setQuery,
  scrollDataProps,
  openFilters,
  onClearAll,
  onListItemClick,
  cashbackCurrency,
  tokenProfile,
  ...restProps
}) => {
  const pageTitle = t(customConfig.getCashbackLabel, {
    symbol: tokenProfile.symbol
  })
  const selectedCategories = Object.keys(checkedCategoriesForFilter).length || 0
  return (
    <>
      <PageTitle title={pageTitle} />
      <Stack spacing={2.5} sx={{ mb: { xs: 3, md: 5 } }}>
        <Box
          sx={{
            gap: 3,
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-end', sm: 'center' },
            mb: 2
          }}
        >
          <SearchBar
            t={t}
            value={query}
            onChange={(value) => {
              setQuery(value)
            }}
            {...restProps}
          />
          <Box sx={{ gap: 1, flexShrink: 0, display: 'flex' }}>
            <CashbackFilters
              t={t}
              onClearAll={onClearAll}
              canReset={selectedCategories}
              filterCategories={filterCategories}
              checkedCategoriesForFilter={checkedCategoriesForFilter}
              onListItemClick={onListItemClick}
              open={openFilters.value}
              onOpen={openFilters.onTrue}
              onClose={openFilters.onFalse}
            />
          </Box>
        </Box>
        {selectedCategories > 0 && (
          <CashbackFiltersResult
            t={t}
            onClearAll={onClearAll}
            filterCategories={filterCategories.filter(
              ({ seoFriendlyId }) => checkedCategoriesForFilter[seoFriendlyId]
            )}
            onListItemClick={onListItemClick}
          />
        )}

        {loading ? (
          <DataLayout>
            <CashbackLoading />
          </DataLayout>
        ) : !cashbackBrands.length && !loading ? (
          <RecordsNotFound
            icon={customConfig.cashbackIcon}
            description={t('cashback.no-search-result')}
          />
        ) : (
          <InfiniteScroll
            dataLength={cashbackBrands.length}
            loader={<LoadingScreen sx={{ mt: { xs: 3, md: 6 } }} />}
            style={{ overflow: 'visible' }}
            {...scrollDataProps}
          >
            <DataLayout>
              {cashbackBrands.map((data, index) => (
                <CashbackBrandCard
                  {...data}
                  t={t}
                  onCashbackShopNowClick={onCashbackShopNowClick}
                  key={index}
                  cashbackCurrency={cashbackCurrency}
                  tokenProfile={tokenProfile}
                  {...restProps}
                />
              ))}
            </DataLayout>
          </InfiniteScroll>
        )}
      </Stack>
    </>
  )
}

export default memo(Cashback)

const CashbackLoading = () => {
  return Array.from(new Array(3)).map((_, index) => (
    <Card key={index}>
      <Box sx={{ p: 3, pb: 2 }}>
        <Skeleton variant='rounded' width={160} height={90} sx={{ mb: 2 }} />
        <Skeleton variant='text' height={24} width='80%' sx={{ mb: 1 }} />
        <Skeleton variant='text' height={16} width='90%' sx={{ mb: 0.5 }} />
        <Skeleton variant='text' height={16} width='90%' sx={{ mb: 0.5 }} />
        <Skeleton variant='text' height={16} width='70%' sx={{ mb: 1.2 }} />
        <Skeleton
          variant='rounded'
          width={150}
          height={24}
          sx={{ borderRadius: 1.5 }}
        />
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Stack
        sx={{
          p: 3,
          alignItems: 'flex-end'
        }}
      >
        <Skeleton
          variant='rounded'
          width={140}
          height={48}
          sx={{ borderRadius: 1.5 }}
        />
      </Stack>
    </Card>
  ))
}

const DataLayout = ({ children }) => {
  return (
    <Box
      sx={{
        gap: 3,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)'
        }
      }}
    >
      {children}
    </Box>
  )
}
